import { CopyOutlined, WarningOutlined } from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import { Alert, Button, message, Space, Tooltip } from 'antd';
import type { User } from '@/types';
import { endImpersonation, getImpersonatedRental } from '@/services/auth';
import { isAdmin } from '@/types/models';

// The one indicator of an Impersonation (docs/ROLES.md), and the one place that ends it.
export const ImpersonationBanner = (): React.JSX.Element | null => {
  const { data: user } = useGetIdentity<User>();
  const impersonated = getImpersonatedRental();

  // Only show for platform admins who are impersonating
  if (!user || !isAdmin(user) || !impersonated) {
    return null;
  }

  const handleCopyRentalId = (): void => {
    void navigator.clipboard.writeText(impersonated.id).then(() => {
      void message.success('Rental ID copied to clipboard');
    });
  };

  return (
    <Alert
      className="impersonation-bar"
      type="warning"
      banner
      showIcon
      icon={<WarningOutlined />}
      message={
        <span className="impersonation-bar__text">
          Acting as <strong>{impersonated.name}</strong>
          <span className="impersonation-bar__note">. All actions are logged.</span>
        </span>
      }
      action={
        <Space size="small">
          <Tooltip title={`Copy rental ID: ${impersonated.id}`}>
            <Button
              size="small"
              type="text"
              aria-label="Copy rental ID"
              icon={<CopyOutlined />}
              onClick={handleCopyRentalId}
            />
          </Tooltip>
          <Button
            size="small"
            danger
            onClick={() => {
              void endImpersonation(impersonated.id);
            }}
          >
            <span>
              Exit<span className="impersonation-bar__note"> Impersonation</span>
            </span>
          </Button>
        </Space>
      }
    />
  );
};
